/**
 * PdfContextMenu Component
 *
 * Context menu for PDF viewer that appears on text selection or right-click.
 * Shows "New Prompt (N)" option to create inline prompts.
 * Ported from orbit-summer-2022-demo context menu implementation.
 */

import React, { useEffect, useState } from "react";

export interface ContextMenuItem {
  title: string;
  shortcutKey: string;
  onClick: () => void;
  isEnabled: boolean;
}

interface PdfContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
}

interface MenuItemProps {
  title: string;
  shortcutKey: string;
  onClick: () => void;
  isEnabled: boolean;
}

function MenuItem({ title, shortcutKey, onClick, isEnabled }: MenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Handle keyboard shortcut
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === shortcutKey.toLowerCase() && isEnabled) {
        e.preventDefault();
        onClick();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [shortcutKey, isEnabled, onClick]);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: 12,
        height: 36,
        margin: 0,
        backgroundColor: "var(--bgPrimary)",
        border: "none",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
        cursor: "pointer",
        position: "relative",
      }}
    >
      {/* Hover overlay */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "var(--hoverLayer)",
            pointerEvents: "none",
          }}
        />
      )}
      <span
        style={{
          fontFamily: "Dr-Bold, system-ui, sans-serif",
          fontSize: 14,
          lineHeight: "17px",
          letterSpacing: "0.04em",
          color: "var(--accentPrimary)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {title}
      </span>
      <ShortcutKey shortcutKey={shortcutKey} hover={isHovered} />
    </button>
  );
}

interface ShortcutKeyProps {
  shortcutKey: string;
  hover: boolean;
}

function ShortcutKey({ shortcutKey, hover }: ShortcutKeyProps) {
  return (
    <div
      style={{
        position: "relative",
        width: 20,
        height: 20,
        background: "var(--bgSecondary)",
        borderRadius: 2,
        border: hover ? "2px solid var(--fgTertiary)" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
      }}
    >
      <span
        style={{
          fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
          fontSize: 15,
          lineHeight: "17px",
          textAlign: "center",
          letterSpacing: "0.03em",
          color: "var(--fgSecondarySmall)",
        }}
      >
        {shortcutKey}
      </span>
    </div>
  );
}

export default function PdfContextMenu({
  items,
  position,
  onClose,
}: PdfContextMenuProps) {
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const menu = document.querySelector(".pdfContextMenu");
      if (menu && e.target instanceof Node && !menu.contains(e.target)) {
        onClose();
      }
    };

    // Small delay to prevent immediate closing from the same click that opened
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      className="pdfContextMenu"
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        zIndex: 10000,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          width: 254,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: 4,
          background: "var(--bgPrimary)",
          border: "3px solid var(--fgTertiary)",
          boxShadow:
            "0px 4px 15px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.07)",
        }}
      >
        {items.map((item, i) => (
          <MenuItem
            key={i}
            title={item.title}
            shortcutKey={item.shortcutKey}
            onClick={item.onClick}
            isEnabled={item.isEnabled}
          />
        ))}
      </div>
    </div>
  );
}
